import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { GMAIL_TOKENS } from "@/features/gmail/constants/designTokens";
import useRouterData from "@/hooks/api/useRouterData";
import { cn } from "@/lib/utils";
import { LayoutDashboard } from "lucide-react";
import { NavLink } from "react-router-dom";

/** Same paths as `public/assets/*` + `TAB_ICON_MAP` (TheIconSelector). */
function sidebarNavAsset(file: string) {
  return `${import.meta.env.BASE_URL}assets/${file}`.replace(/\/{2,}/g, "/");
}

function SidebarNavRasterIcon({ src, alt }: { src: string; alt: string }) {
  return (
    <span className="inline-flex size-5 shrink-0 items-center justify-center [&>img]:size-5 [&>img]:object-contain">
      <img src={src} alt={alt} loading="lazy" decoding="async" />
    </span>
  );
}

/**
 * Dashboard / Whatsapp / Gmail links at the top of the sidebar.
 *
 * Each one shows ONLY when the role carries that tab. The `/tabs` payload
 * (`platform/tabs/tabs.services.ts`) sends `dashboard` / `whatsapp` / `gmail`
 * as null for a role without the route key, and `buildDynamicRoutes` then
 * registers no route for it — so an unconditional link here landed a
 * Counsellor without WhatsApp on the 404 page (owner, 2026-10-28, Skymark
 * "Viewing as Abhishek"). Same source of truth as the accordion sections
 * (`useSidebarSections` reads the same query), so link and route agree.
 */
export default function AppSidebarDashBoard({
  isCollapsed,
}: {
  isCollapsed: boolean;
}) {
  const { tabs } = useRouterData();
  const showDashboard = Boolean(tabs?.dashboard);
  const showWhatsapp = Boolean(tabs?.whatsapp);
  const showGmail = Boolean(tabs?.gmail);

  // Nothing granted (or the tabs are still loading, like the sections below):
  // no group at all, so no empty gap above the first section.
  if (!showDashboard && !showWhatsapp && !showGmail) return null;

  return (
    <SidebarGroup
      className={cn(
        "transition-[padding] duration-200 ease-linear mt-4 -mb-4 cursor-pointer",
        isCollapsed && "px-0"
      )}
    >
      <SidebarGroupContent
        className={cn(
          "transition-all duration-200 ease-linear",
          isCollapsed && "flex items-center justify-center"
        )}
      >
        <SidebarMenu
          className={cn(
            "transition-all duration-200 ease-linear",
            isCollapsed && "w-full items-center"
          )}
        >
          {showDashboard && (
            <NavLink to={"/"}>
              {({ isActive }) => (
                <SidebarMenuItem>
                  {/* Left-side active indicator bar - full height, extends to sidebar edge */}
                  {isActive && !isCollapsed && (
                    <div
                      className="absolute -left-1 top-0 bottom-0 w-[8px] rounded-tr-full rounded-br-full z-10"
                      style={{ background: "var(--color-primary-gradient)" }}
                    />
                  )}
                  <SidebarMenuButton
                    isActive={isActive}
                    tooltip="Dashboard"
                    className={cn(
                      "transition-all duration-200 ease-linear pl-7",
                      isCollapsed && "justify-center text-center gap-0 w-full"
                    )}
                  >
                    <LayoutDashboard />
                    <span className="text-b2">Dashboard</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </NavLink>
          )}
          {showWhatsapp && (
            <NavLink to={"/whatsapp"}>
              {({ isActive }) => (
                <SidebarMenuItem>
                  {/* Left-side active indicator bar - full height, extends to sidebar edge */}
                  {isActive && !isCollapsed && (
                    <div
                      className="absolute -left-1 top-0 bottom-0 w-[8px] rounded-tr-full rounded-br-full z-10"
                      style={{ background: "var(--color-primary-gradient)" }}
                    />
                  )}
                  <SidebarMenuButton
                    isActive={isActive}
                    tooltip="Whatsapp"
                    className={cn(
                      "transition-all duration-200 ease-linear pl-7",
                      isCollapsed && "justify-center text-center gap-0 w-full"
                    )}
                  >
                    <SidebarNavRasterIcon
                      src={sidebarNavAsset("whatsapp.png")}
                      alt=""
                    />
                    <span className="text-b2">Whatsapp</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </NavLink>
          )}
          {showGmail && (
            <NavLink to={"/gmail"}>
              {({ isActive }) => (
                <SidebarMenuItem>
                  {isActive && !isCollapsed && (
                    <div
                      className="absolute -left-1 top-0 bottom-0 w-[8px] rounded-tr-full rounded-br-full z-10"
                      style={{ background: GMAIL_TOKENS.gmailRed }}
                    />
                  )}
                  <SidebarMenuButton
                    isActive={isActive}
                    tooltip="Gmail"
                    className={cn(
                      "transition-all duration-200 ease-linear pl-7",
                      isActive &&
                        "!bg-[#c2e7ff] !text-[#001d35] hover:!bg-[#b4dffd]",
                      isCollapsed && "justify-center text-center gap-0 w-full"
                    )}
                  >
                    <SidebarNavRasterIcon
                      src={sidebarNavAsset("gmail.png")}
                      alt=""
                    />
                    <span className="text-b2">Gmail</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </NavLink>
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
